import React, { useEffect, useState } from 'react';
import {
  ANNOTATION_TYPE_OPTIONS,
  annotationSaveCounts,
  annotationSideForShortcut,
  annotationTypeForShortcut,
} from '../../util/contactAnnotations';

function shortcutBlockedByTarget(target) {
  if (!target || typeof target.tagName !== 'string') return false;
  const tag = target.tagName;
  if (tag === 'TEXTAREA' || target.isContentEditable) return true;
  if (tag !== 'INPUT') return false;
  const type = String(target.getAttribute('type') || 'text').toLowerCase();
  return type !== 'button' && type !== 'submit' && type !== 'checkbox' && type !== 'radio' && type !== 'range';
}

function AnnotateContactsBar({
  active,
  onToggle,
  typeKey,
  side,
  onTypeChange,
  annotations,
  savedAnnotations,
  saving,
  status,
  onSave,
  onPlaceAtPlayhead,
}) {
  const marks = Array.isArray(annotations) ? annotations : [];
  const counts = annotationSaveCounts(marks, savedAnnotations);
  const saveConfirmed = status === 'saved' && !counts.dirty;
  const [showSaved, setShowSaved] = useState(false);
  const modeLabel = ANNOTATION_TYPE_OPTIONS.find((opt) => opt.value === typeKey)?.label || '';
  const sideLabel = side === 'left' ? 'Left' : side === 'right' ? 'Right' : '';

  useEffect(() => {
    if (!saveConfirmed) {
      setShowSaved(false);
      return undefined;
    }
    setShowSaved(true);
    const timer = setTimeout(() => setShowSaved(false), 3000);
    return () => clearTimeout(timer);
  }, [saveConfirmed]);

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (shortcutBlockedByTarget(event.target)) return;
      const sideKey = annotationSideForShortcut(event.key);
      if (sideKey) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof onPlaceAtPlayhead === 'function') onPlaceAtPlayhead(sideKey);
        return;
      }
      const next = annotationTypeForShortcut(event.key);
      if (!next) return;
      event.preventDefault();
      onTypeChange(next);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [active, onTypeChange, onPlaceAtPlayhead]);

  return (
    <div className={`annotate-bar${active ? ' annotate-bar--active' : ''}`}>
      <div className="annotate-bar__row">
        <button
          type="button"
          className="annotate-bar__toggle"
          aria-pressed={active}
          onClick={onToggle}
        >
          Annotate
        </button>
        {active ? (
          <p className="annotate-bar__mode" role="status">
            Annotate mode. Click a graph to place the selected foot contact on that side.
            Click a marker of the selected event to remove it.
            Scrub with the video timeline without adding a mark.
            Z heel contact, X toe contact, C heel off, V toe off.
            A places the selected event at the current frame on the left foot, S on the right.
            Press the same key again to remove it.
          </p>
        ) : null}
      </div>
      {active ? (
        <div className="annotate-bar__tools">
          <label>
            Event
            <select aria-label="Contact event" value={typeKey} onChange={(e) => onTypeChange(e.target.value)}>
              {ANNOTATION_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
        </div>
      ) : null}
      {active ? (
        <div className="annotate-save-bar" role="region" aria-label="Save annotations">
          {modeLabel ? (
            <span className="annotate-save-mode">
              {sideLabel ? `[${modeLabel} - ${sideLabel}]` : `[${modeLabel}]`}
            </span>
          ) : null}
          <span className="annotate-save-unsaved">{counts.unsaved} unsaved</span>
          <span className="annotate-save-saved">{counts.saved} saved</span>
          {status === 'failed' ? <span className="annotate-save-status">Save failed</span> : null}
          <button
            type="button"
            className={showSaved ? 'is-saved' : undefined}
            onClick={onSave}
            disabled={saving || !counts.dirty || marks.length === 0}
          >
            {saving ? 'Saving…' : showSaved ? 'Saved' : 'Save'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default AnnotateContactsBar;
